import React from "react";
import { Box, Typography } from "@mui/material";
import "./ContinuousProgressLoader.css";

const ContinuousProgressLoader = ({
  message = "Processing records in background...",
  variant = "wave", // 'wave', 'pulse', 'flow', 'shimmer', 'progress'
  color = "primary",
  size = "medium", // 'small', 'medium', 'large'
  showMessage = true,
  className = "",
  style = {},
  total = 0, // For progress variant
  processed = 0, // For progress variant
}) => {
  const getLoaderClass = () => {
    return `continuous-loader continuous-loader--${variant} continuous-loader--${size} continuous-loader--${color}`;
  };

  const renderWaveLoader = () => (
    <Box className={`${getLoaderClass()} ${className}`} style={style}>
      <div className="wave-container">
        <div className="wave-track">
          <div className="wave-progress">
            <div className="wave-shimmer"></div>
          </div>
        </div>
        <div className="wave-dots">
          <span className="dot dot-1"></span>
          <span className="dot dot-2"></span>
          <span className="dot dot-3"></span>
        </div>
      </div>
      {showMessage && (
        <Typography variant="body2" className="loader-message">
          {message}
        </Typography>
      )}
    </Box>
  );

  const renderPulseLoader = () => (
    <Box className={`${getLoaderClass()} ${className}`} style={style}>
      <div className="pulse-container">
        <div className="pulse-track">
          <div className="pulse-fill"></div>
          <div className="pulse-glow"></div>
        </div>
        <div className="pulse-rings">
          <div className="ring ring-1"></div>
          <div className="ring ring-2"></div>
          <div className="ring ring-3"></div>
        </div>
      </div>
      {showMessage && (
        <Typography variant="body2" className="loader-message">
          {message}
        </Typography>
      )}
    </Box>
  );

  const renderFlowLoader = () => (
    <Box className={`${getLoaderClass()} ${className}`} style={style}>
      <div className="flow-container">
        <div className="flow-track">
          <div className="flow-stream">
            <div className="particle particle-1"></div>
            <div className="particle particle-2"></div>
            <div className="particle particle-3"></div>
            <div className="particle particle-4"></div>
            <div className="particle particle-5"></div>
          </div>
          <div className="flow-gradient"></div>
        </div>
      </div>
      {showMessage && (
        <Typography variant="body2" className="loader-message">
          {message}
        </Typography>
      )}
    </Box>
  );

  const renderShimmerLoader = () => (
    <Box className={`${getLoaderClass()} ${className}`} style={style}>
      <div className="shimmer-container">
        <div className="shimmer-track">
          <div className="shimmer-base"></div>
          <div className="shimmer-highlight"></div>
          <div className="shimmer-overlay"></div>
        </div>
        <div className="shimmer-indicator">
          <div className="indicator-bar"></div>
        </div>
      </div>
      {showMessage && (
        <Typography variant="body2" className="loader-message">
          {message}
        </Typography>
      )}
    </Box>
  );

  const renderProgressLoader = () => (
    <Box className={`${getLoaderClass()} ${className}`} style={style}>
      <div className="progress-container">
        <div className="progress-track">
          <div
            className="progress-fill"
            style={{
              width: `${((processed || 0) / (total || 1)) * 100}%`,
            }}
          >
            <div className="progress-shine"></div>
          </div>
        </div>
        <div className="progress-stats">
          <Typography variant="body2" className="progress-text">
            {processed || 0} / {total || 0} records processed
          </Typography>
          <Typography variant="body1" className="progress-percentage">
            {Math.round(((processed || 0) / (total || 1)) * 100)}%
          </Typography>
        </div>
      </div>
      {showMessage && (
        <Typography variant="body2" className="loader-message">
          {message}
        </Typography>
      )}
    </Box>
  );

  const renderLoader = () => {
    switch (variant) {
      case "pulse":
        return renderPulseLoader();
      case "flow":
        return renderFlowLoader();
      case "shimmer":
        return renderShimmerLoader();
      case "progress":
        return renderProgressLoader();
      default:
        return renderWaveLoader();
    }
  };

  return renderLoader();
};

export default ContinuousProgressLoader;
